import type { ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import {
  ArrowsOutLineHorizontalIcon,
  CaretLeftIcon,
  CaretRightIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { ListDetailSplitController } from './useListDetailSplit';

export interface ListDetailSplitProps<T> {
  /** Selection + animation state, from `useListDetailSplit`. */
  split: ListDetailSplitController;
  /** Box classes for the grid — its height, most importantly. */
  className?: string;
  /** The rows backing the rail: the same page of data the table shows. */
  items: T[];
  getItemId: (item: T) => string;
  renderRailRow: (args: {
    item: T;
    selected: boolean;
    /** True while the rail is in its narrow strip form. */
    collapsed: boolean;
    onSelect: () => void;
  }) => ReactNode;
  isLoading?: boolean;
  /** Names the collection in the rail header, e.g. "Threads". */
  railTitle: ReactNode;
  railEmptyMessage: ReactNode;
  /** DOM id for the detail pane, so rail rows can point `aria-controls` at it. */
  detailPaneId: string;
  /** The full table, shown whenever nothing is selected. */
  table: ReactNode;
  renderDetail: (detailId: string) => ReactNode;
  /** Pager, rendered on a mirrored track so it stays under the list column. */
  pagination?: ReactNode;
}

/**
 * A list ↔ detail split view: the full table fills the width until a row is
 * picked, then collapses to a mail-style rail on the left while the detail
 * expands in from the right.
 *
 * Both panes stay mounted; only the grid column tracks animate. Closed is
 * `1fr 0fr` (table fills, detail track collapsed); open hands most of the
 * width to the detail. fr-only tracks so the widths actually interpolate;
 * `min-w-0` + `overflow-hidden` lets the detail track reach zero and clips
 * each pane during the sweep, so the list/table content can swap instantly
 * underneath the clip — the eye follows the motion.
 *
 * Pair with `useListDetailSplit`, which owns the state and the refs wired up
 * here. See `CollectionThreadsTable` and `ARCashReceiptsPage` for callers.
 */
export function ListDetailSplit<T>({
  split,
  className,
  items,
  getItemId,
  renderRailRow,
  isLoading = false,
  railTitle,
  railEmptyMessage,
  detailPaneId,
  table,
  renderDetail,
  pagination,
}: ListDetailSplitProps<T>) {
  const {
    detailId,
    isOpen,
    railCollapsed,
    gridColumns,
    listPaneRef,
    close,
    onTracksTransitionEnd,
  } = split;

  return (
    <>
      <div
        className={cn(
          'grid gap-3 transition-[grid-template-columns] duration-300 ease-in-out motion-reduce:transition-none',
          className,
        )}
        style={{ gridTemplateColumns: gridColumns }}
        onTransitionEnd={onTracksTransitionEnd}
      >
        <div ref={listPaneRef} className="min-w-0 h-full overflow-hidden">
          {/*
            Gate the rail on detailId (deferred), NOT isOpen (urgent), so the
            click's urgent commit only flips the grid columns — the heavy
            table→rail swap is deferred to the transition and happens after the
            animation has started, instead of blocking the first paint. The
            table stays mounted and simply shrinks/clips in the narrowing track
            until the rail swaps in. On close, isOpen is false so this falls
            back to the table (reveal).
          */}
          {isOpen && detailId ? (
            <SplitRail
              split={split}
              items={items}
              getItemId={getItemId}
              renderRailRow={renderRailRow}
              isLoading={isLoading}
              railTitle={railTitle}
              railEmptyMessage={railEmptyMessage}
            />
          ) : (
            table
          )}
        </div>

        <div className="min-w-0 h-full overflow-hidden">
          {(isOpen || detailId) && (
            <SplitDetailPane
              id={detailPaneId}
              detailId={detailId}
              onClose={close}
              renderDetail={renderDetail}
            />
          )}
        </div>
      </div>

      {/*
        Pager sits under the list column: same track template as the grid
        above, so it spans full-width when closed and shifts left under the
        list when a row is open. Shown whenever the full table is visible
        (!isOpen); only hidden when the detail is open AND the rail is
        collapsed too narrow to fit it. Gating on railCollapsed alone would
        leave the full table pager-less after something auto-collapsed the
        rail, since railCollapsed isn't reset on close.
      */}
      {pagination && (
        <div
          className="grid gap-3 transition-[grid-template-columns] duration-300 ease-in-out motion-reduce:transition-none"
          style={{ gridTemplateColumns: gridColumns }}
        >
          <div className="min-w-0">
            {(!isOpen || !railCollapsed) && pagination}
          </div>
          <div />
        </div>
      )}
    </>
  );
}

/**
 * The rail the table collapses into: a header carrying the width controls,
 * over a scrolling list of the same rows the table was showing.
 */
function SplitRail<T>({
  split,
  items,
  getItemId,
  renderRailRow,
  isLoading,
  railTitle,
  railEmptyMessage,
}: Pick<
  ListDetailSplitProps<T>,
  | 'split'
  | 'items'
  | 'getItemId'
  | 'renderRailRow'
  | 'railTitle'
  | 'railEmptyMessage'
> & { isLoading: boolean }) {
  const gt = useGT();
  const {
    selectedId,
    railCollapsed,
    setRailCollapsed,
    railScrollRef,
    selectedRowRef,
    select,
    close,
  } = split;

  // Accessible name + tooltip for the icon-only rail toggle (shared so the
  // two stay in sync). The header already names the collection, so these stay
  // generic rather than repeating it.
  const railToggleLabel = railCollapsed
    ? gt('Expand list', { context: 'button, expand the collapsed list rail' })
    : gt('Collapse list', { context: 'button, collapse the list rail' });
  // The rail-side twin of the detail pane's ✕: closes the detail and hands the
  // full width back to the table, so closing doesn't require mousing across
  // the screen to the far edge.
  const expandListLabel = gt('Close and expand the list', {
    context: 'button, close the detail pane and return to the full table',
  });

  return (
    <div className="h-full flex flex-col rounded-md border bg-card overflow-hidden">
      <div className="flex items-center gap-1 border-b px-2 py-1.5">
        <span className="text-xs font-medium text-muted-foreground">
          {railTitle}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="ml-auto h-6 w-6 text-muted-foreground"
          onClick={() => setRailCollapsed((v) => !v)}
          aria-label={railToggleLabel}
          title={railToggleLabel}
        >
          {railCollapsed ? (
            <CaretRightIcon className="h-4 w-4" />
          ) : (
            <CaretLeftIcon className="h-4 w-4" />
          )}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground"
          onClick={close}
          aria-label={expandListLabel}
          title={expandListLabel}
        >
          <ArrowsOutLineHorizontalIcon className="h-4 w-4" />
        </Button>
      </div>
      <div ref={railScrollRef} className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-2 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            {railEmptyMessage}
          </div>
        ) : (
          <ul className="divide-y">
            {items.map((item) => {
              const id = getItemId(item);
              const selected = id === selectedId;
              return (
                <li key={id} ref={selected ? selectedRowRef : undefined}>
                  {renderRailRow({
                    item,
                    selected,
                    collapsed: railCollapsed,
                    onSelect: () => select(id),
                  })}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/** The right-hand pane: a bounded box with its own close affordance. */
function SplitDetailPane({
  id,
  detailId,
  onClose,
  renderDetail,
}: {
  id: string;
  detailId: string | null;
  onClose: () => void;
  renderDetail: (detailId: string) => ReactNode;
}) {
  return (
    <div
      id={id}
      // Bounded box only — the detail view's own content region scrolls.
      className="relative h-full overflow-hidden rounded-md border bg-card"
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 z-10 h-7 w-7"
        onClick={onClose}
      >
        <span className="sr-only">
          <T context="button, close the detail pane">Close</T>
        </span>
        <XIcon className="h-4 w-4" />
      </Button>
      {detailId ? (
        renderDetail(detailId)
      ) : (
        // Urgent placeholder shown for the one frame between the grid starting
        // to expand and the deferred detail mount committing, so the growing
        // pane is never blank.
        <div className="px-4 py-3 space-y-3">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      )}
    </div>
  );
}
